import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { teas } from '../data/mock';
import { Lock, Package, Mail, Users, Trash2, RefreshCw, Power, Download, BarChart3, Plus, Image, Eye, EyeOff, Gift, Edit2, Leaf } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for bundles
const BUNDLE_ICONS = ['Sparkles', 'Moon', 'Zap', 'Heart', 'Gift', 'Star'];
const BUNDLE_COLORS = [
  { name: 'Purple', color: '#6366f1', bgColor: '#eef2ff' },
  { name: 'Orange', color: '#f59e0b', bgColor: '#fffbeb' },
  { name: 'Green', color: '#10b981', bgColor: '#ecfdf5' },
  { name: 'Red', color: '#ef4444', bgColor: '#fef2f2' },
  { name: 'Blue', color: '#3b82f6', bgColor: '#eff6ff' },
  { name: 'Brown', color: '#8b5c2f', bgColor: '#fef3c7' },
];

export const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [stockStatus, setStockStatus] = useState({});
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [prices, setPrices] = useState({});
  const [editingPrice, setEditingPrice] = useState(null);
  const [tempPrice, setTempPrice] = useState('');
  const [analytics, setAnalytics] = useState({ total_visits: 0, today_visits: 0, last_7_days: [] });
  const [labelVisibility, setLabelVisibility] = useState({});
  const [customProducts, setCustomProducts] = useState([]);
  const [promoBanner, setPromoBanner] = useState({ text: 'Buy 4 Bags, Get FREE Shipping!', enabled: true });
  const [editingPromo, setEditingPromo] = useState(false);
  const [tempPromoText, setTempPromoText] = useState('');
  const [bundles, setBundles] = useState([]);
  const [editingBundle, setEditingBundle] = useState(null);
  const [announcement, setAnnouncement] = useState({ text: '', enabled: false });
  const [countdown, setCountdown] = useState({ enabled: false, endDate: '', title: '', subtitle: '' });
  const [accessories, setAccessories] = useState([]);
  const [newAccessory, setNewAccessory] = useState({ name: '', description: '', price: '', image: '', category: 'general', inStock: true, featured: false });
  const [editingAccessory, setEditingAccessory] = useState(null);
  const [newBundle, setNewBundle] = useState({
    name: '',
    description: '',
    icon: 'Sparkles',
    products: '',
    originalPrice: '',
    bundlePrice: '',
    savings: '',
    color: '#6366f1',
    bgColor: '#eef2ff',
    enabled: true
  });
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    purpose: '',
    price: '',
    weight: '5oz',
    imageFront: '',
    imageBack: '',
    benefits: '',
    warning: '',
    inStock: true
  });
  const [addingProduct, setAddingProduct] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const adminSession = sessionStorage.getItem('mudbutt_admin');
    if (adminSession === 'true') {
      setIsLoggedIn(true);
      loadData();
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [stockRes, contactsRes, subscribersRes, maintenanceRes, pricesRes, analyticsRes, labelsRes, customProductsRes, promoRes, bundlesRes, announcementRes, countdownRes, accessoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stock`),
        fetch(`${API_URL}/api/contacts`),
        fetch(`${API_URL}/api/subscribers`),
        fetch(`${API_URL}/api/admin/maintenance`),
        fetch(`${API_URL}/api/admin/prices`),
        fetch(`${API_URL}/api/admin/analytics`),
        fetch(`${API_URL}/api/admin/labels`),
        fetch(`${API_URL}/api/products/custom`),
        fetch(`${API_URL}/api/promo-banner`),
        fetch(`${API_URL}/api/bundles`),
        fetch(`${API_URL}/api/announcement`),
        fetch(`${API_URL}/api/countdown`),
        fetch(`${API_URL}/api/admin/accessories`)
      ]);
      
      const stockData = await stockRes.json();
      const contactsData = await contactsRes.json();
      const subscribersData = await subscribersRes.json();
      const maintenanceData = await maintenanceRes.json();
      const pricesData = await pricesRes.json();
      const analyticsData = await analyticsRes.json();
      const labelsData = await labelsRes.json();
      const customProductsData = await customProductsRes.json();
      const promoData = await promoRes.json();
      const bundlesData = await bundlesRes.json();
      const announcementData = await announcementRes.json();
      const countdownData = await countdownRes.json();
      const accessoriesData = await accessoriesRes.json();
      
      setStockStatus(stockData);
      setContacts(contactsData);
      setSubscribers(subscribersData);
      setMaintenanceMode(maintenanceData.enabled);
      setPrices(pricesData);
      setAnalytics(analyticsData);
      setLabelVisibility(labelsData);
      setCustomProducts(customProductsData);
      setPromoBanner(promoData);
      setBundles(bundlesData);
      setAnnouncement(announcementData);
      setCountdown(countdownData);
      setAccessories(accessoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        sessionStorage.setItem('mudbutt_admin', 'true');
        setIsLoggedIn(true);
        loadData();
      } else {
        setLoginError('Invalid password');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('mudbutt_admin');
    setIsLoggedIn(false);
    setPassword('');
  };

  const getStockStatus = (product) => {
    // Returns status string: "in_stock", "out_of_stock", or "coming_soon"
    if (stockStatus.hasOwnProperty(product.id)) {
      const status = stockStatus[product.id];
      // Handle backwards compatibility - if it's a boolean, convert it
      if (typeof status === 'boolean') {
        return status ? 'in_stock' : 'out_of_stock';
      }
      return status;
    }
    // Default to in stock if product has inStock property
    if (product.inStock === false) return 'out_of_stock';
    return 'in_stock';
  };

  const cycleStockStatus = async (productId, currentStatus) => {
    // Cycle: in_stock -> out_of_stock -> coming_soon -> in_stock
    const statusCycle = {
      'in_stock': 'out_of_stock',
      'out_of_stock': 'coming_soon',
      'coming_soon': 'in_stock'
    };
    const newStatus = statusCycle[currentStatus] || 'in_stock';
    
    try {
      await fetch(`${API_URL}/api/admin/stock/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      setStockStatus(prev => ({ ...prev, [productId]: newStatus }));
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const toggleStock = async (productId, currentStatus) => {
    const newStatus = !currentStatus;
    
    try {
      await fetch(`${API_URL}/api/admin/stock/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inStock: newStatus })
      });
      
      setStockStatus(prev => ({ ...prev, [productId]: newStatus ? 'in_stock' : 'out_of_stock' }));
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact submission?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/contacts/${contactId}`, {
        method: 'DELETE'
      });
      setContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const deleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Delete this subscriber?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/subscribers/${subscriberId}`, {
        method: 'DELETE'
      });
      setSubscribers(prev => prev.filter(s => s.id !== subscriberId));
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  const exportSubscribers = () => {
    // Filter only active subscribers
    const activeSubscribers = subscribers.filter(s => s.subscribed);
    
    // Create CSV content
    const headers = ['Email', 'Subscribed Date'];
    const rows = activeSubscribers.map(s => [
      s.email,
      new Date(s.timestamp).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mudbutt-brew-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const toggleMaintenanceMode = async () => {
    const newStatus = !maintenanceMode;
    try {
      await fetch(`${API_URL}/api/admin/maintenance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newStatus })
      });
      setMaintenanceMode(newStatus);
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
    }
  };

  const getPrice = (product) => {
    if (prices.hasOwnProperty(product.id)) {
      return prices[product.id];
    }
    return product.price;
  };

  const startEditingPrice = (product) => {
    setEditingPrice(product.id);
    setTempPrice(getPrice(product).replace('$', ''));
  };

  const savePrice = async (productId) => {
    const newPrice = `$${tempPrice}`;
    try {
      await fetch(`${API_URL}/api/admin/prices/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: newPrice })
      });
      setPrices(prev => ({ ...prev, [productId]: newPrice }));
      setEditingPrice(null);
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const cancelEditingPrice = () => {
    setEditingPrice(null);
    setTempPrice('');
  };

  // Promo Banner functions
  const startEditingPromo = () => {
    setTempPromoText(promoBanner.text);
    setEditingPromo(true);
  };

  const savePromoBanner = async () => {
    try {
      await fetch(`${API_URL}/api/admin/promo-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: tempPromoText, enabled: promoBanner.enabled })
      });
      setPromoBanner(prev => ({ ...prev, text: tempPromoText }));
      setEditingPromo(false);
    } catch (error) {
      console.error('Error updating promo banner:', error);
    }
  };

  const togglePromoBanner = async () => {
    const newEnabled = !promoBanner.enabled;
    try {
      await fetch(`${API_URL}/api/admin/promo-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: promoBanner.text, enabled: newEnabled })
      });
      setPromoBanner(prev => ({ ...prev, enabled: newEnabled }));
    } catch (error) {
      console.error('Error toggling promo banner:', error);
    }
  };

  // Announcement functions
  const updateAnnouncement = async (text, enabled) => {
    try {
      await fetch(`${API_URL}/api/admin/announcement`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, enabled })
      });
      setAnnouncement({ text, enabled });
    } catch (error) {
      console.error('Error updating announcement:', error);
    }
  };

  // Countdown functions
  const updateCountdown = async (data) => {
    try {
      await fetch(`${API_URL}/api/admin/countdown`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      setCountdown(data);
    } catch (error) {
      console.error('Error updating countdown:', error);
    }
  };

  const setAllStock = async (status) => {
    try {
      await fetch(`${API_URL}/api/admin/stock-all`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      // Update local state for all products
      const newStockStatus = {};
      teas.forEach(tea => {
        newStockStatus[tea.id] = status;
      });
      customProducts.forEach(p => {
        newStockStatus[p.id] = status;
      });
      setStockStatus(newStockStatus);
    } catch (error) {
      console.error('Error updating all stock:', error);
    }
  };

  // Label visibility functions
  const getLabelVisibility = (productId) => {
    if (labelVisibility.hasOwnProperty(productId)) {
      return labelVisibility[productId];
    }
    return true; // Default: show labels
  };

  const toggleLabelVisibility = async (productId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await fetch(`${API_URL}/api/admin/labels/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ showLabels: newStatus })
      });
      setLabelVisibility(prev => ({ ...prev, [productId]: newStatus }));
    } catch (error) {
      console.error('Error updating label visibility:', error);
    }
  };

  const setAllLabels = async (showLabels) => {
    try {
      await fetch(`${API_URL}/api/admin/labels-all`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ showLabels })
      });
      const newLabelVisibility = {};
      teas.forEach(tea => {
        newLabelVisibility[tea.id] = showLabels;
      });
      customProducts.forEach(p => {
        newLabelVisibility[p.id] = showLabels;
      });
      setLabelVisibility(newLabelVisibility);
    } catch (error) {
      console.error('Error updating all labels:', error);
    }
  };

  // Add product functions
  const handleNewProductChange = (field, value) => {
    setNewProduct(prev => ({ ...prev, [field]: value }));
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price || !newProduct.description) {
      alert('Please fill in Name, Price, and Description');
      return;
    }
    
    setAddingProduct(true);
    try {
      const productData = {
        ...newProduct,
        price: newProduct.price.startsWith('$') ? newProduct.price : `$${newProduct.price}`,
        benefits: newProduct.benefits ? newProduct.benefits.split(',').map(b => b.trim()).filter(b => b) : []
      };
      
      const response = await fetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });
      
      const data = await response.json();
      if (data.success) {
        setCustomProducts(prev => [...prev, data.product]);
        setNewProduct({
          name: '',
          description: '',
          purpose: '',
          price: '',
          weight: '5oz',
          imageFront: '',
          imageBack: '',
          benefits: '',
          warning: '',
          inStock: true
        });
        alert('Product added successfully!');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error adding product. Please try again.');
    }
    setAddingProduct(false);
  };

  // Bundle management functions
  const handleNewBundleChange = (field, value) => {
    setNewBundle(prev => ({ ...prev, [field]: value }));
  };

  const handleAddBundle = async (e) => {
    e.preventDefault();
    if (!newBundle.name || !newBundle.bundlePrice || !newBundle.products) {
      alert('Please fill in Name, Bundle Price, and Products');
      return;
    }
    
    try {
      const bundleData = {
        ...newBundle,
        products: newBundle.products.split(',').map(p => p.trim()).filter(p => p)
      };
      
      const response = await fetch(`${API_URL}/api/admin/bundles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });
      
      const data = await response.json();
      if (data.success) {
        setBundles(prev => [...prev, data.bundle]);
        setNewBundle({
          name: '',
          description: '',
          icon: 'Sparkles',
          products: '',
          originalPrice: '',
          bundlePrice: '',
          savings: '',
          color: '#6366f1',
          bgColor: '#eef2ff',
          enabled: true
        });
        alert('Bundle added successfully!');
      }
    } catch (error) {
      console.error('Error adding bundle:', error);
      alert('Error adding bundle. Please try again.');
    }
  };

  const toggleBundleEnabled = async (bundleId, currentEnabled) => {
    try {
      await fetch(`${API_URL}/api/admin/bundles/${bundleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !currentEnabled })
      });
      setBundles(prev => prev.map(b => b.id === bundleId ? { ...b, enabled: !currentEnabled } : b));
    } catch (error) {
      console.error('Error toggling bundle:', error);
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm('Delete this bundle?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/bundles/${bundleId}`, {
        method: 'DELETE'
      });
      setBundles(prev => prev.filter(b => b.id !== bundleId));
    } catch (error) {
      console.error('Error deleting bundle:', error);
    }
  };

  const startEditBundle = (bundle) => {
    setEditingBundle(bundle.id);
    setNewBundle({
      ...bundle,
      products: bundle.products.join(', ')
    });
  };

  const saveEditBundle = async () => {
    try {
      const bundleData = {
        ...newBundle,
        products: newBundle.products.split(',').map(p => p.trim()).filter(p => p)
      };
      
      await fetch(`${API_URL}/api/admin/bundles/${editingBundle}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });
      
      setBundles(prev => prev.map(b => b.id === editingBundle ? { ...bundleData, id: editingBundle } : b));
      setEditingBundle(null);
      setNewBundle({
        name: '',
        description: '',
        icon: 'Sparkles',
        products: '',
        originalPrice: '',
        bundlePrice: '',
        savings: '',
        color: '#6366f1',
        bgColor: '#eef2ff',
        enabled: true
      });
    } catch (error) {
      console.error('Error updating bundle:', error);
    }
  };

  const cancelEditBundle = () => {
    setEditingBundle(null);
    setNewBundle({
      name: '',
      description: '',
      icon: 'Sparkles',
      products: '',
      originalPrice: '',
      bundlePrice: '',
      savings: '',
      color: '#6366f1',
      bgColor: '#eef2ff',
      enabled: true
    });
  };

  const deleteCustomProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE'
      });
      setCustomProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Combine default and custom products
  const allProducts = [...teas, ...customProducts];

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f5e9d0' }}>
        <Card className="w-full max-w-md border-2" style={{ borderColor: '#c2a357' }}>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-4 rounded-full" style={{ backgroundColor: '#5e3a1c' }}>
              <Lock className="w-8 h-8" style={{ color: '#c2a357' }} />
            </div>
            <CardTitle style={{ color: '#5e3a1c', fontFamily: 'Georgia, serif' }}>
              Admin Dashboard
            </CardTitle>
            <p style={{ color: '#8b5c2f' }}>Enter your password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" style={{ color: '#5e3a1c' }}>Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="border-2"
                  style={{ borderColor: '#c2a357' }}
                  required
                />
              </div>
              {loginError && (
                <p className="text-red-600 text-sm">{loginError}</p>
              )}
              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5e9d0' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 shadow-md" style={{ backgroundColor: '#5e3a1c' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold" style={{ color: '#f5e9d0', fontFamily: 'Georgia, serif' }}>
            Mudbutt Brew Admin
          </h1>
          <div className="flex gap-2 items-center">
            <Button
              onClick={loadData}
              variant="outline"
              size="sm"
              disabled={loading}
              style={{ borderColor: '#c2a357', color: '#c2a357' }}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              style={{ borderColor: '#c2a357', color: '#c2a357' }}
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Site Status Card */}
        <Card className="mb-6 border-2" style={{ borderColor: maintenanceMode ? '#dc3545' : '#28a745' }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Power className={`w-6 h-6 ${maintenanceMode ? 'text-red-500' : 'text-green-500'}`} />
                <div>
                  <p className="font-bold" style={{ color: '#5e3a1c' }}>
                    Website Status: {maintenanceMode ? 'OFFLINE' : 'LIVE'}
                  </p>
                  <p className="text-sm" style={{ color: '#8b5c2f' }}>
                    {maintenanceMode 
                      ? 'Visitors see a "Coming Soon" page' 
                      : 'Your website is visible to everyone'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-bold ${maintenanceMode ? 'text-red-600' : 'text-green-600'}`}>
                  {maintenanceMode ? 'Maintenance Mode' : 'Live'}
                </span>
                <Switch
                  checked={!maintenanceMode}
                  onCheckedChange={toggleMaintenanceMode}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Promo Banner Settings */}
        <Card className="mb-6 border-2" style={{ borderColor: '#c2a357' }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div 
                  className="px-4 py-2 rounded-full font-bold text-sm"
                  style={{ 
                    backgroundColor: promoBanner.enabled ? '#3a692b' : '#6b7280', 
                    color: '#f5e9d0' 
                  }}
                >
                  {promoBanner.enabled ? promoBanner.text : 'Banner Hidden'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {editingPromo ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={tempPromoText}
                      onChange={(e) => setTempPromoText(e.target.value)}
                      className="w-64"
                      style={{ borderColor: '#c2a357' }}
                      placeholder="e.g., Buy 3 Bags, Get FREE Shipping!"
                    />
                    <Button
                      onClick={savePromoBanner}
                      size="sm"
                      style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => setEditingPromo(false)}
                      size="sm"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={startEditingPromo}
                    size="sm"
                    variant="outline"
                    style={{ borderColor: '#c2a357', color: '#5e3a1c' }}
                  >
                    Edit Text
                  </Button>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: '#8b5c2f' }}>
                    {promoBanner.enabled ? 'Visible' : 'Hidden'}
                  </span>
                  <Switch
                    checked={promoBanner.enabled}
                    onCheckedChange={togglePromoBanner}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Announcement Bar Settings */}
        <Card className="mb-6 border-2" style={{ borderColor: '#c2a357' }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="font-bold" style={{ color: '#5e3a1c' }}>Announcement Bar:</span>
                <Input
                  value={announcement.text}
                  onChange={(e) => setAnnouncement(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="e.g., 🔥 New: Single Herbs Now Available!"
                  className="w-64 md:w-96"
                  style={{ borderColor: '#c2a357' }}
                />
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => updateAnnouncement(announcement.text, announcement.enabled)}
                  size="sm"
                  style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                >
                  Save
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: '#8b5c2f' }}>
                    {announcement.enabled ? 'Visible' : 'Hidden'}
                  </span>
                  <Switch
                    checked={announcement.enabled}
                    onCheckedChange={(checked) => updateAnnouncement(announcement.text, checked)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Countdown Timer Settings */}
        <Card className="mb-6 border-2" style={{ borderColor: '#c2a357' }}>
          <CardContent className="py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold" style={{ color: '#5e3a1c' }}>Countdown Timer:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: '#8b5c2f' }}>
                    {countdown.enabled ? 'Active' : 'Off'}
                  </span>
                  <Switch
                    checked={countdown.enabled}
                    onCheckedChange={(checked) => updateCountdown({ ...countdown, enabled: checked })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs" style={{ color: '#8b5c2f' }}>End Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={countdown.endDate ? countdown.endDate.slice(0, 16) : ''}
                    onChange={(e) => setCountdown(prev => ({ ...prev, endDate: new Date(e.target.value).toISOString() }))}
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" style={{ color: '#8b5c2f' }}>Title</Label>
                  <Input
                    value={countdown.title}
                    onChange={(e) => setCountdown(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Limited Time Offer!"
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" style={{ color: '#8b5c2f' }}>Subtitle</Label>
                  <Input
                    value={countdown.subtitle}
                    onChange={(e) => setCountdown(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Order now and save!"
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => updateCountdown(countdown)}
                    style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                    className="w-full"
                  >
                    Save Timer
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            onClick={() => setActiveTab('products')}
            style={activeTab === 'products' 
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <Package className="w-4 h-4 mr-2" />
            Products ({allProducts.length})
          </Button>
          <Button
            onClick={() => setActiveTab('addProduct')}
            style={activeTab === 'addProduct' 
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
          <Button
            onClick={() => setActiveTab('bundles')}
            style={activeTab === 'bundles' 
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <Gift className="w-4 h-4 mr-2" />
            Bundles ({bundles.length})
          </Button>
          <Button
            onClick={() => setActiveTab('contacts')}
            style={activeTab === 'contacts'
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <Mail className="w-4 h-4 mr-2" />
            Contacts ({contacts.length})
          </Button>
          <Button
            onClick={() => setActiveTab('subscribers')}
            style={activeTab === 'subscribers'
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <Users className="w-4 h-4 mr-2" />
            Subscribers ({subscribers.filter(s => s.subscribed).length})
          </Button>
          <Button
            onClick={() => setActiveTab('analytics')}
            style={activeTab === 'analytics'
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => setActiveTab('accessories')}
            style={activeTab === 'accessories'
              ? { backgroundColor: '#8b5c2f', color: '#f5e9d0' }
              : { backgroundColor: 'white', color: '#5e3a1c', border: '2px solid #c2a357' }
            }
            data-testid="admin-accessories-tab"
          >
            <Leaf className="w-4 h-4 mr-2" />
            Accessories ({accessories.length})
          </Button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <CardTitle style={{ color: '#5e3a1c' }}>Product Stock & Label Management</CardTitle>
                  <p style={{ color: '#8b5c2f' }}>Toggle stock, labels, and edit prices</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => setAllStock('in_stock')}
                    size="sm"
                    style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                  >
                    All In Stock
                  </Button>
                  <Button
                    onClick={() => setAllStock('out_of_stock')}
                    size="sm"
                    style={{ backgroundColor: '#dc3545', color: '#f5e9d0' }}
                  >
                    All Out of Stock
                  </Button>
                  <Button
                    onClick={() => setAllStock('coming_soon')}
                    size="sm"
                    style={{ backgroundColor: '#f59e0b', color: '#ffffff' }}
                  >
                    All Coming Soon
                  </Button>
                  <Button
                    onClick={() => setAllLabels(true)}
                    size="sm"
                    style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Show All Labels
                  </Button>
                  <Button
                    onClick={() => setAllLabels(false)}
                    size="sm"
                    variant="outline"
                    style={{ borderColor: '#8b5c2f', color: '#8b5c2f' }}
                  >
                    <EyeOff className="w-4 h-4 mr-1" />
                    Hide All Labels
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allProducts.map((product) => {
                  const status = getStockStatus(product);
                  const currentPrice = getPrice(product);
                  const showLabels = getLabelVisibility(product.id);
                  const isCustom = product.id >= 100;
                  
                  // Get background color based on status
                  const bgColor = status === 'in_stock' ? '#e8f5e9' : 
                                  status === 'coming_soon' ? '#fef3c7' : '#ffebee';
                  
                  // Get status display info
                  const statusInfo = {
                    'in_stock': { label: 'In Stock', color: 'text-green-700', bg: '#3a692b' },
                    'out_of_stock': { label: 'Out', color: 'text-red-700', bg: '#dc3545' },
                    'coming_soon': { label: 'Coming Soon', color: 'text-amber-700', bg: '#f59e0b' }
                  }[status] || { label: 'In Stock', color: 'text-green-700', bg: '#3a692b' };
                  
                  return (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: bgColor }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium w-8" style={{ color: '#8b5c2f' }}>
                          #{product.id}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium" style={{ color: '#5e3a1c' }}>{product.name}</p>
                            {isCustom && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#c2a357', color: '#5e3a1c' }}>
                                Custom
                              </span>
                            )}
                          </div>
                          {editingPrice === product.id ? (
                            <div className="flex items-center gap-2 mt-1">
                              <span style={{ color: '#8b5c2f' }}>$</span>
                              <Input
                                type="text"
                                value={tempPrice}
                                onChange={(e) => setTempPrice(e.target.value)}
                                className="w-20 h-7 text-sm"
                                style={{ borderColor: '#c2a357' }}
                              />
                              <Button
                                onClick={() => savePrice(product.id)}
                                size="sm"
                                className="h-7 px-2"
                                style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                              >
                                Save
                              </Button>
                              <Button
                                onClick={cancelEditingPrice}
                                size="sm"
                                variant="outline"
                                className="h-7 px-2"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <p 
                              className="text-sm cursor-pointer hover:underline" 
                              style={{ color: '#8b5c2f' }}
                              onClick={() => startEditingPrice(product)}
                              title="Click to edit price"
                            >
                              {currentPrice} (click to edit)
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Label Toggle */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: '#8b5c2f' }}>
                            {showLabels ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </span>
                          <span className="text-xs hidden sm:inline" style={{ color: showLabels ? '#3a692b' : '#8b5c2f' }}>
                            Labels
                          </span>
                          <Switch
                            checked={showLabels}
                            onCheckedChange={() => toggleLabelVisibility(product.id, showLabels)}
                          />
                        </div>
                        {/* Stock Status Button */}
                        <button
                          onClick={() => cycleStockStatus(product.id, status)}
                          className="px-3 py-1 rounded-full text-sm font-medium transition-all hover:opacity-80"
                          style={{ backgroundColor: statusInfo.bg, color: '#ffffff' }}
                          title="Click to cycle: In Stock → Out → Coming Soon"
                        >
                          {statusInfo.label}
                        </button>
                        {/* Delete button for custom products */}
                        {isCustom && (
                          <Button
                            onClick={() => deleteCustomProduct(product.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Product Tab */}
        {activeTab === 'addProduct' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <CardTitle style={{ color: '#5e3a1c' }}>Add New Product</CardTitle>
              <p style={{ color: '#8b5c2f' }}>Create a new tea product for your store</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddProduct} className="space-y-4 max-w-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Product Name *</Label>
                    <Input
                      value={newProduct.name}
                      onChange={(e) => handleNewProductChange('name', e.target.value)}
                      placeholder="e.g., Herbal Sunrise"
                      style={{ borderColor: '#c2a357' }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Price *</Label>
                    <Input
                      value={newProduct.price}
                      onChange={(e) => handleNewProductChange('price', e.target.value)}
                      placeholder="14.99"
                      style={{ borderColor: '#c2a357' }}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Description *</Label>
                  <Textarea
                    value={newProduct.description}
                    onChange={(e) => handleNewProductChange('description', e.target.value)}
                    placeholder="A refreshing blend perfect for..."
                    style={{ borderColor: '#c2a357' }}
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Purpose/Tagline</Label>
                    <Input
                      value={newProduct.purpose}
                      onChange={(e) => handleNewProductChange('purpose', e.target.value)}
                      placeholder="e.g., Morning Energy"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Weight</Label>
                    <Input
                      value={newProduct.weight}
                      onChange={(e) => handleNewProductChange('weight', e.target.value)}
                      placeholder="5oz"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>
                      <Image className="w-4 h-4 inline mr-1" />
                      Front Label Image URL
                    </Label>
                    <Input
                      value={newProduct.imageFront}
                      onChange={(e) => handleNewProductChange('imageFront', e.target.value)}
                      placeholder="https://..."
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>
                      <Image className="w-4 h-4 inline mr-1" />
                      Back Label Image URL
                    </Label>
                    <Input
                      value={newProduct.imageBack}
                      onChange={(e) => handleNewProductChange('imageBack', e.target.value)}
                      placeholder="https://..."
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Benefits (comma-separated)</Label>
                  <Input
                    value={newProduct.benefits}
                    onChange={(e) => handleNewProductChange('benefits', e.target.value)}
                    placeholder="Energy, Focus, Immune Support"
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Warning (optional)</Label>
                  <Input
                    value={newProduct.warning}
                    onChange={(e) => handleNewProductChange('warning', e.target.value)}
                    placeholder="e.g., Contains caffeine"
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={newProduct.inStock}
                    onCheckedChange={(checked) => handleNewProductChange('inStock', checked)}
                  />
                  <Label style={{ color: '#5e3a1c' }}>In Stock</Label>
                </div>

                <Button
                  type="submit"
                  disabled={addingProduct}
                  style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                  className="w-full md:w-auto"
                >
                  {addingProduct ? 'Adding...' : 'Add Product'}
                </Button>
              </form>

              {/* Custom Products List */}
              {customProducts.length > 0 && (
                <div className="mt-8 pt-6 border-t" style={{ borderColor: '#c2a357' }}>
                  <h3 className="font-bold mb-4" style={{ color: '#5e3a1c' }}>Your Custom Products ({customProducts.length})</h3>
                  <div className="space-y-2">
                    {customProducts.map(product => (
                      <div key={product.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#f5e9d0' }}>
                        <div>
                          <p className="font-medium" style={{ color: '#5e3a1c' }}>{product.name}</p>
                          <p className="text-sm" style={{ color: '#8b5c2f' }}>{product.price} - {product.description}</p>
                        </div>
                        <Button
                          onClick={() => deleteCustomProduct(product.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bundles Tab */}
        {activeTab === 'bundles' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <CardTitle style={{ color: '#5e3a1c' }}>Bundle Management</CardTitle>
              <p style={{ color: '#8b5c2f' }}>Create and manage product bundles</p>
            </CardHeader>
            <CardContent>
              {/* Add/Edit Bundle Form */}
              <form onSubmit={editingBundle ? (e) => { e.preventDefault(); saveEditBundle(); } : handleAddBundle} className="space-y-4 mb-8 p-4 rounded-lg" style={{ backgroundColor: '#f5e9d0' }}>
                <h3 className="font-bold" style={{ color: '#5e3a1c' }}>
                  {editingBundle ? 'Edit Bundle' : 'Add New Bundle'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Bundle Name *</Label>
                    <Input
                      value={newBundle.name}
                      onChange={(e) => handleNewBundleChange('name', e.target.value)}
                      placeholder="e.g., Sleep Collection"
                      style={{ borderColor: '#c2a357' }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Icon</Label>
                    <select
                      value={newBundle.icon}
                      onChange={(e) => handleNewBundleChange('icon', e.target.value)}
                      className="w-full p-2 rounded border-2"
                      style={{ borderColor: '#c2a357' }}
                    >
                      {BUNDLE_ICONS.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Description</Label>
                  <Input
                    value={newBundle.description}
                    onChange={(e) => handleNewBundleChange('description', e.target.value)}
                    placeholder="e.g., Rest easy with our calming trio..."
                    style={{ borderColor: '#c2a357' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Products (comma-separated) *</Label>
                  <Input
                    value={newBundle.products}
                    onChange={(e) => handleNewBundleChange('products', e.target.value)}
                    placeholder="e.g., Sleep Whisper, Calm Storm, Cortisol Crush"
                    style={{ borderColor: '#c2a357' }}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Original Price</Label>
                    <Input
                      value={newBundle.originalPrice}
                      onChange={(e) => handleNewBundleChange('originalPrice', e.target.value)}
                      placeholder="$50.97"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Bundle Price *</Label>
                    <Input
                      value={newBundle.bundlePrice}
                      onChange={(e) => handleNewBundleChange('bundlePrice', e.target.value)}
                      placeholder="$42.99"
                      style={{ borderColor: '#c2a357' }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: '#5e3a1c' }}>Savings Text</Label>
                    <Input
                      value={newBundle.savings}
                      onChange={(e) => handleNewBundleChange('savings', e.target.value)}
                      placeholder="Save $8!"
                      style={{ borderColor: '#c2a357' }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label style={{ color: '#5e3a1c' }}>Color Theme</Label>
                  <div className="flex gap-2 flex-wrap">
                    {BUNDLE_COLORS.map(c => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => {
                          handleNewBundleChange('color', c.color);
                          handleNewBundleChange('bgColor', c.bgColor);
                        }}
                        className={`w-8 h-8 rounded-full border-2 ${newBundle.color === c.color ? 'ring-2 ring-offset-2' : ''}`}
                        style={{ backgroundColor: c.color, borderColor: c.color }}
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={newBundle.enabled}
                    onCheckedChange={(checked) => handleNewBundleChange('enabled', checked)}
                  />
                  <Label style={{ color: '#5e3a1c' }}>Enabled (visible on website)</Label>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                  >
                    {editingBundle ? 'Save Changes' : 'Add Bundle'}
                  </Button>
                  {editingBundle && (
                    <Button
                      type="button"
                      onClick={cancelEditBundle}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>

              {/* Existing Bundles List */}
              <h3 className="font-bold mb-4" style={{ color: '#5e3a1c' }}>Current Bundles ({bundles.length})</h3>
              <div className="space-y-3">
                {bundles.map((bundle) => (
                  <div
                    key={bundle.id}
                    className="flex items-center justify-between p-4 rounded-lg border-2"
                    style={{ 
                      borderColor: bundle.color,
                      backgroundColor: bundle.enabled ? bundle.bgColor : '#f3f4f6'
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold" style={{ color: '#5e3a1c' }}>{bundle.name}</span>
                        {!bundle.enabled && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-300 text-gray-600">Hidden</span>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: '#8b5c2f' }}>
                        {bundle.products?.join(', ')}
                      </p>
                      <p className="text-sm font-bold" style={{ color: bundle.color }}>
                        {bundle.bundlePrice} <span className="line-through text-gray-400 font-normal">{bundle.originalPrice}</span> {bundle.savings}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={bundle.enabled}
                        onCheckedChange={() => toggleBundleEnabled(bundle.id, bundle.enabled)}
                      />
                      <Button
                        onClick={() => startEditBundle(bundle)}
                        size="sm"
                        variant="outline"
                        style={{ borderColor: '#c2a357' }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => deleteBundle(bundle.id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <CardTitle style={{ color: '#5e3a1c' }}>Contact Submissions</CardTitle>
              <p style={{ color: '#8b5c2f' }}>Messages from customers</p>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#8b5c2f' }}>No contact submissions yet</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-4 rounded-lg border-2"
                      style={{ borderColor: '#c2a357', backgroundColor: 'white' }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-bold" style={{ color: '#5e3a1c' }}>{contact.name}</p>
                          <a href={`mailto:${contact.email}`} className="text-sm underline" style={{ color: '#8b5c2f' }}>
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: '#8b5c2f' }}>
                            {new Date(contact.timestamp).toLocaleDateString()}
                          </span>
                          <Button
                            onClick={() => deleteContact(contact.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm font-medium mb-1" style={{ color: '#5e3a1c' }}>{contact.subject}</p>
                      <p className="text-sm" style={{ color: '#8b5c2f' }}>{contact.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle style={{ color: '#5e3a1c' }}>Newsletter Subscribers</CardTitle>
                  <p style={{ color: '#8b5c2f' }}>People subscribed to your bi-weekly newsletter</p>
                </div>
                {subscribers.filter(s => s.subscribed).length > 0 && (
                  <Button
                    onClick={exportSubscribers}
                    style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {subscribers.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#8b5c2f' }}>No subscribers yet</p>
              ) : (
                <div className="space-y-2">
                  {subscribers.map((subscriber) => (
                    <div
                      key={subscriber.id}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: subscriber.subscribed ? '#e8f5e9' : '#ffebee' }}
                    >
                      <div className="flex items-center gap-3">
                        <a href={`mailto:${subscriber.email}`} className="underline" style={{ color: '#5e3a1c' }}>
                          {subscriber.email}
                        </a>
                        <span className={`text-xs px-2 py-1 rounded ${subscriber.subscribed ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                          {subscriber.subscribed ? 'Active' : 'Unsubscribed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: '#8b5c2f' }}>
                          {new Date(subscriber.timestamp).toLocaleDateString()}
                        </span>
                        <Button
                          onClick={() => deleteSubscriber(subscriber.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <CardTitle style={{ color: '#5e3a1c' }}>Website Analytics</CardTitle>
              <p style={{ color: '#8b5c2f' }}>Track your website visitors</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-6 rounded-lg text-center" style={{ backgroundColor: '#e8f5e9' }}>
                  <p className="text-4xl font-bold" style={{ color: '#3a692b' }}>{analytics.total_visits}</p>
                  <p className="text-sm mt-2" style={{ color: '#5e3a1c' }}>Total Visits</p>
                </div>
                <div className="p-6 rounded-lg text-center" style={{ backgroundColor: '#fff3e0' }}>
                  <p className="text-4xl font-bold" style={{ color: '#e65100' }}>{analytics.today_visits}</p>
                  <p className="text-sm mt-2" style={{ color: '#5e3a1c' }}>Today's Visits</p>
                </div>
              </div>
              
              <h3 className="font-bold mb-4" style={{ color: '#5e3a1c' }}>Last 7 Days</h3>
              <div className="space-y-2">
                {analytics.last_7_days && analytics.last_7_days.map((day, index) => (
                  <div 
                    key={day.date} 
                    className="flex items-center justify-between p-3 rounded"
                    style={{ backgroundColor: index === 0 ? '#e8f5e9' : '#f5f5f5' }}
                  >
                    <span style={{ color: '#5e3a1c' }}>
                      {index === 0 ? 'Today' : new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="font-bold" style={{ color: '#8b5c2f' }}>{day.visits} visits</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accessories Tab */}
        {activeTab === 'accessories' && (
          <Card className="border-2" style={{ borderColor: '#c2a357' }}>
            <CardHeader>
              <CardTitle style={{ color: '#5e3a1c' }}>Tea Accessories & Equipment</CardTitle>
              <p style={{ color: '#8b5c2f' }}>Manage presses, infusers, kettles, mugs, and more</p>
            </CardHeader>
            <CardContent>
              {/* Add New Accessory Form */}
              <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: '#f5e9d0', border: '1px solid #c2a357' }}>
                <h3 className="font-bold mb-4" style={{ color: '#5e3a1c' }}>Add New Accessory</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label style={{ color: '#5e3a1c' }}>Name *</Label>
                    <Input
                      value={newAccessory.name}
                      onChange={(e) => setNewAccessory(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., French Press 34oz"
                      style={{ borderColor: '#c2a357' }}
                      data-testid="acc-name-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#5e3a1c' }}>Price *</Label>
                    <Input
                      value={newAccessory.price}
                      onChange={(e) => setNewAccessory(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="e.g., $29.99"
                      style={{ borderColor: '#c2a357' }}
                      data-testid="acc-price-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#5e3a1c' }}>Category</Label>
                    <select
                      value={newAccessory.category}
                      onChange={(e) => setNewAccessory(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2"
                      style={{ borderColor: '#c2a357' }}
                      data-testid="acc-category-select"
                    >
                      <option value="press">French Presses</option>
                      <option value="infuser">Infusers & Strainers</option>
                      <option value="kettle">Kettles</option>
                      <option value="mug">Mugs & Cups</option>
                      <option value="general">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label style={{ color: '#5e3a1c' }}>Image URL</Label>
                    <Input
                      value={newAccessory.image}
                      onChange={(e) => setNewAccessory(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="Paste image URL (optional)"
                      style={{ borderColor: '#c2a357' }}
                      data-testid="acc-image-input"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Label style={{ color: '#5e3a1c' }}>Description *</Label>
                  <Textarea
                    value={newAccessory.description}
                    onChange={(e) => setNewAccessory(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the product..."
                    style={{ borderColor: '#c2a357' }}
                    data-testid="acc-desc-input"
                  />
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newAccessory.inStock}
                      onCheckedChange={(val) => setNewAccessory(prev => ({ ...prev, inStock: val }))}
                    />
                    <Label style={{ color: '#5e3a1c' }}>In Stock</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newAccessory.featured}
                      onCheckedChange={(val) => setNewAccessory(prev => ({ ...prev, featured: val }))}
                    />
                    <Label style={{ color: '#5e3a1c' }}>Featured</Label>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    if (!newAccessory.name || !newAccessory.price || !newAccessory.description) return;
                    try {
                      const res = await fetch(`${API_URL}/api/admin/accessories`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newAccessory)
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setAccessories(prev => [...prev, data.accessory]);
                        setNewAccessory({ name: '', description: '', price: '', image: '', category: 'general', inStock: true, featured: false });
                      }
                    } catch (err) {
                      console.error('Error creating accessory:', err);
                    }
                  }}
                  style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                  data-testid="acc-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Accessory
                </Button>
              </div>

              {/* Accessories List */}
              {accessories.length === 0 ? (
                <div className="text-center py-8">
                  <Leaf className="w-12 h-12 mx-auto mb-3" style={{ color: '#c2a357' }} />
                  <p style={{ color: '#8b5c2f' }}>No accessories added yet. Add your first product above!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accessories.map((acc) => (
                    <div
                      key={acc.id}
                      className="flex items-center justify-between p-4 rounded-lg border"
                      style={{ borderColor: '#c2a357', backgroundColor: editingAccessory === acc.id ? '#fef3c7' : 'white' }}
                      data-testid={`acc-item-${acc.id}`}
                    >
                      {editingAccessory === acc.id ? (
                        <div className="w-full space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input
                              defaultValue={acc.name}
                              id={`acc-edit-name-${acc.id}`}
                              placeholder="Name"
                              style={{ borderColor: '#c2a357' }}
                            />
                            <Input
                              defaultValue={acc.price}
                              id={`acc-edit-price-${acc.id}`}
                              placeholder="Price"
                              style={{ borderColor: '#c2a357' }}
                            />
                            <select
                              defaultValue={acc.category}
                              id={`acc-edit-cat-${acc.id}`}
                              className="border rounded-md px-3 py-2"
                              style={{ borderColor: '#c2a357' }}
                            >
                              <option value="press">French Presses</option>
                              <option value="infuser">Infusers & Strainers</option>
                              <option value="kettle">Kettles</option>
                              <option value="mug">Mugs & Cups</option>
                              <option value="general">Other</option>
                            </select>
                          </div>
                          <Textarea
                            defaultValue={acc.description}
                            id={`acc-edit-desc-${acc.id}`}
                            placeholder="Description"
                            style={{ borderColor: '#c2a357' }}
                          />
                          <Input
                            defaultValue={acc.image}
                            id={`acc-edit-img-${acc.id}`}
                            placeholder="Image URL"
                            style={{ borderColor: '#c2a357' }}
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={async () => {
                                const name = document.getElementById(`acc-edit-name-${acc.id}`).value;
                                const price = document.getElementById(`acc-edit-price-${acc.id}`).value;
                                const category = document.getElementById(`acc-edit-cat-${acc.id}`).value;
                                const description = document.getElementById(`acc-edit-desc-${acc.id}`).value;
                                const image = document.getElementById(`acc-edit-img-${acc.id}`).value;
                                try {
                                  await fetch(`${API_URL}/api/admin/accessories/${acc.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ name, price, category, description, image })
                                  });
                                  setAccessories(prev => prev.map(a => a.id === acc.id ? { ...a, name, price, category, description, image } : a));
                                  setEditingAccessory(null);
                                } catch (err) {
                                  console.error('Error updating accessory:', err);
                                }
                              }}
                              size="sm"
                              style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                            >
                              Save
                            </Button>
                            <Button
                              onClick={() => setEditingAccessory(null)}
                              size="sm"
                              variant="outline"
                              style={{ borderColor: '#c2a357', color: '#5e3a1c' }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-4 flex-1">
                            {acc.image ? (
                              <img src={acc.image} alt={acc.name} className="w-14 h-14 object-contain rounded" />
                            ) : (
                              <div className="w-14 h-14 rounded flex items-center justify-center" style={{ backgroundColor: '#f5e9d0' }}>
                                <Leaf className="w-6 h-6" style={{ color: '#c2a357' }} />
                              </div>
                            )}
                            <div>
                              <p className="font-bold" style={{ color: '#5e3a1c' }}>{acc.name}</p>
                              <p className="text-sm" style={{ color: '#8b5c2f' }}>{acc.category} &middot; {acc.price}</p>
                              <p className="text-xs" style={{ color: acc.inStock ? '#3a692b' : '#dc2626' }}>
                                {acc.inStock ? 'In Stock' : 'Out of Stock'}
                                {acc.featured && ' \u2022 Featured'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={async () => {
                                try {
                                  await fetch(`${API_URL}/api/admin/accessories/${acc.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ inStock: !acc.inStock })
                                  });
                                  setAccessories(prev => prev.map(a => a.id === acc.id ? { ...a, inStock: !a.inStock } : a));
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                              size="sm"
                              variant="outline"
                              style={{ borderColor: acc.inStock ? '#3a692b' : '#dc2626', color: acc.inStock ? '#3a692b' : '#dc2626' }}
                            >
                              {acc.inStock ? 'In Stock' : 'Out'}
                            </Button>
                            <Button
                              onClick={() => setEditingAccessory(acc.id)}
                              size="sm"
                              variant="outline"
                              style={{ borderColor: '#c2a357', color: '#5e3a1c' }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={async () => {
                                if (!window.confirm(`Delete "${acc.name}"?`)) return;
                                try {
                                  await fetch(`${API_URL}/api/admin/accessories/${acc.id}`, { method: 'DELETE' });
                                  setAccessories(prev => prev.filter(a => a.id !== acc.id));
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                              size="sm"
                              variant="outline"
                              style={{ borderColor: '#dc2626', color: '#dc2626' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
